/**
 * **信用卡还款** —— 从日常账户转一笔到信用卡（批次 `94` · 批 `106-B` 接单一原子接口）
 *
 * ## 🔴 为什么还款 = 转账，而不是一笔支出
 *
 * 还款是**钱换了个位置**（储蓄卡 → 信用卡），不是花掉。若记成支出：
 * ① 「支出」口径凭空多出一笔"还信用卡"，月度支出总额直接失真；
 * ② 信用卡账单那边又会把它当消费再算一遍 ⇒ **同一笔钱被算两次**。
 * ⇒ 走 2 腿转账 + `excludeFromStats = true`（`DR-004` / 规格 §22.9 的既有口径，
 * 与 `贷款还款` 里"本金不计收支"同源）。批 `106-B` 起这个形态由**服务端**落
 * （`POST /credit-bills/repay`），端侧只发金额。
 *
 * ## 🔴 批 `106-B`：走单一原子接口，不再自己拼双腿
 *
 * `AC-B2.49`：实付转账 + 优惠落腿 + 写 waiver 表**一个事务**完成。端侧若拆成
 * 「createTransaction + createWaiver」两次 POST，中途失败会留下"转了钱没记优惠"
 * 的半截状态（批 104「分期动作非原子」的教训不重蹈）。
 *
 * ## 🔴 净额法（契约层锁定）
 *
 * `amount` 是**实付**（已扣优惠）。银行侧事实：用户转 ¥450 + 优惠 ¥50 抵 ¥500 账单。
 * 拆成「转 ¥500 再退 ¥50」会多出一条假退款腿（冲减本期消费 ⇒ 下期账单反而变小）。
 * ⇒ 端侧默认值 = `due − 优惠`，且**不再做加减**（服务端算账单、端侧只填数）。
 *
 * ## ⚠️ 不在本对话框里做的
 *
 * - **不做分期 / 最低还款**：P1。
 */

import { useState, type ReactElement } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  centsToYuanText,
  currencySymbolOf,
  toCents,
  type AccountView,
  type CreditBillsView,
} from '@jiaying/shared';
import { repayCreditBill } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { localToday } from '../app/local-day.js';
import { yuanTextToCentsOrNull } from '../features/txn-draft.js';
import { AccountPicker } from './AccountPicker.js';

export interface CreditRepayDialogProps {
  /** 目标信用卡（`null` = 不渲染 —— 由调用方先取对象再判空，不用非空断言） */
  item: CreditBillsView;
  /** 可作付款方的账户（调用方已按币种 / 类型过滤） */
  payers: readonly AccountView[];
  onClose: () => void;
  onDone: () => void;
}

/** 优惠类型的四个常用项（其余走"其他"手填 —— 契约层 `kind` 不收窄取值） */
const WAIVER_KIND_OPTIONS = ['还款金', '立减', '活动减免', '其他'] as const;

/**
 * 🔴 **外壳与内容分离**（与 `LoanRepayDialog` 同一课）：`Dialog` 走 Portal，
 * 静态渲染**拿不到内容** ⇒ spec 直接渲染本 Body（`LoanRepayDialog.spec` 同款）。
 */
export function CreditRepayDialog({ item, payers, onClose, onDone }: CreditRepayDialogProps): ReactElement {
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" data-testid="credit-repay-dialog">
      <CreditRepayDialogBody item={item} payers={payers} onClose={onClose} onDone={onDone} />
    </Dialog>
  );
}

export function CreditRepayDialogBody({ item, payers, onClose, onDone }: CreditRepayDialogProps): ReactElement {
  const qc = useQueryClient();
  const sym = currencySymbolOf(item.currency);
  /**
   * 默认金额 = 本期应还 − 本期优惠（净额法 · `AC-B2.49`）。
   * 没有账期时用 |余额|（那是"欠多少"的另一个可信来源）。
   * 🔴 走 `centsToYuanText`（定点整除），不写 `/100`。
   */
  const currentWaiver = (item.current?.waiverApplied ?? 0) as number;
  const defaultCents = (item.current?.due ?? -item.balance) as number;
  const defaultNetCents = Math.max(0, defaultCents - currentWaiver);
  const [amountText, setAmountText] = useState(
    defaultNetCents > 0 ? centsToYuanText(toCents(defaultNetCents)) : '',
  );
  const [payerId, setPayerId] = useState('');
  // 🆕 优惠字段（批 `106-B`）：金额空 = 无优惠；有金额才展开类型 / 备注
  const [waiverText, setWaiverText] = useState('');
  const [waiverKind, setWaiverKind] = useState('');
  const [waiverNote, setWaiverNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cents = yuanTextToCentsOrNull(amountText);
  const amountBad = amountText.trim() === '' || cents === null || (cents as number) <= 0;
  const waiverCents = waiverText.trim() === '' ? 0 : yuanTextToCentsOrNull(waiverText);
  const waiverBad = waiverText.trim() !== '' && (waiverCents === null || (waiverCents as number) <= 0);

  const pay = useMutation({
    mutationFn: () =>
      repayCreditBill({
        accountId: item.accountId,
        payerAccountId: payerId,
        date: localToday(),
        // 🔴 净额法：实付（端侧不再做 due − waiver 的服务端校验 —— 服务端只挡形态）
        amount: cents as number,
        ...(waiverCents !== null && (waiverCents as number) > 0
          ? {
              waiver: {
                amount: waiverCents as number,
                ...(waiverKind === '' ? {} : { kind: waiverKind }),
                ...(waiverNote.trim() === '' ? {} : { note: waiverNote.trim() }),
              },
              // 优惠归属期 = 当期账单日（本对话框只服务"还当期"场景；
              // 补记往期优惠走账单明细页的「记优惠」）
              billDay: item.current?.billDay ?? item.unbilled?.windowStart ?? '',
            }
          : {}),
      }),
    onSuccess: () => {
      // 余额 / 账单 / 流水 / 净资产四处一起失效（还款同时改这四处）
      void qc.invalidateQueries({ queryKey: ['creditBills'] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['transactions'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
      onDone();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const pickers = payers.map((a) => ({
    id: a.id,
    name: a.name,
    ownerName: a.ownerName,
    currency: a.currency,
  }));

  return (
    <>
      <DialogTitle>还款 · {item.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            从日常账户转一笔到这张卡，记为<b>转账</b>（不计入收支）。本期应还{' '}
            {defaultCents > 0 ? `${sym}${centsToYuanText(toCents(defaultCents))}` : '0.00'}
            {currentWaiver > 0 && <> · 优惠 {sym}{centsToYuanText(toCents(currentWaiver))}</>}
            {item.current !== null && (
              <>
                {' '}
                · 账单日 {item.current.billDay} · 还款日 {item.current.repaymentDate}
              </>
            )}
          </Typography>

          <AccountPicker
            label="从哪个账户还款"
            accounts={pickers}
            value={payerId}
            onChange={(id) => {
              setPayerId(id);
              setError(null);
            }}
            required
          />

          <TextField
            required
            inputMode="decimal"
            label={`还款金额（${sym} · 实付）`}
            placeholder="0.00"
            value={amountText}
            onChange={(e) => {
              setAmountText(e.target.value);
              setError(null);
            }}
            error={amountText !== '' && amountBad}
            helperText={
              amountText !== '' && amountBad
                ? '最多两位小数，且需大于 0'
                : '默认按本期应还扣优惠后填写，可改成部分还款 / 多还'
            }
            slotProps={{ htmlInput: { 'data-testid': 'credit-repay-amount' } }}
          />

          <Divider />
          <Typography variant="subtitle2">本次还款优惠（可选）</Typography>
          <Typography variant="caption" color="text.secondary">
            银行给了还款金 / 立减？填进来 —— 优惠直接抵掉本期该还的钱，不产生收支。
          </Typography>

          <TextField
            inputMode="decimal"
            label={`优惠金额（${sym}）`}
            placeholder="留空 = 无优惠"
            value={waiverText}
            onChange={(e) => {
              setWaiverText(e.target.value);
              setError(null);
            }}
            error={waiverBad}
            helperText={waiverBad ? '最多两位小数，且需大于 0' : '计入本期账单减免，卡面「优惠」同步显示'}
            slotProps={{ htmlInput: { 'data-testid': 'credit-repay-waiver-amount' } }}
          />

          {waiverCents !== null && (waiverCents as number) > 0 && (
            <>
              <TextField
                select
                label="优惠类型"
                value={waiverKind}
                onChange={(e) => setWaiverKind(e.target.value)}
                slotProps={{ htmlInput: { 'data-testid': 'credit-repay-waiver-kind' } }}
              >
                <option value="">（不填）</option>
                {WAIVER_KIND_OPTIONS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </TextField>
              <TextField
                label="优惠备注"
                value={waiverNote}
                onChange={(e) => setWaiverNote(e.target.value)}
                slotProps={{ htmlInput: { 'data-testid': 'credit-repay-waiver-note' } }}
              />
            </>
          )}

          {error !== null && (
            <Alert severity="error" data-testid="credit-repay-error">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pay.isPending}>
          取消
        </Button>
        <Button
          variant="contained"
          disabled={pay.isPending || amountBad || waiverBad || payerId === ''}
          onClick={() => {
            setError(null);
            pay.mutate();
          }}
          data-testid="credit-repay-submit"
        >
          {pay.isPending ? '提交中…' : '确认还款'}
        </Button>
      </DialogActions>
    </>
  );
}
